/**
 * UI language catalog for the backoffice.
 *
 * IMPORTANT — language is a SEPARATE axis from currency. Currency keeps driving
 * money formatting via `~/i18n/currency` (CURRENCY_LOCALE_MAP); this module owns
 * the UI *language* (which strings the user sees) and is independent of it.
 *
 * The catalog is backoffice-owned and rolled out incrementally by developers —
 * Indian languages first. Each user's choice persists on `User.preferredLanguage`
 * and falls back to English when unset or unsupported.
 *
 * `nativeName` is what the language switcher shows, in that language's own script
 * (so a Tamil speaker recognises "தமிழ்", not "Tamil").
 */

export interface Language {

  /** ISO 639-1 code stored in `User.preferredLanguage` and used as the i18next lng. */
  code: string;

  /** Name in the language's own script — shown in the switcher. */
  nativeName: string;
}

/** The languages the backoffice can render. Indian languages first, then European. */
export const SUPPORTED_LANGUAGES: Language[] = [
  { code: "en", nativeName: "English" },
  { code: "hi", nativeName: "हिन्दी" },
  { code: "ta", nativeName: "தமிழ்" },
  { code: "te", nativeName: "తెలుగు" },
  { code: "kn", nativeName: "ಕನ್ನಡ" },
  { code: "de", nativeName: "Deutsch" },
  { code: "fr", nativeName: "Français" },
  { code: "es", nativeName: "Español" },
  { code: "it", nativeName: "Italiano" },
  { code: "pt", nativeName: "Português" },
];

/** English — used on first login with no saved preference and as the i18next fallback. */
export const DEFAULT_LANGUAGE = "en";

/** Just the codes, for i18next `supportedLngs`. */
export const SUPPORTED_LANGUAGE_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);

/** Resolve a stored/raw value to a supported language code; unknown/missing → English. */
export const resolveLanguage = (lang?: string | null): string => {
  if (!lang) return DEFAULT_LANGUAGE;
  // Match on the base code (e.g. "en-US" → "en") so a stray BCP-47 value still resolves.
  const base = lang.toLowerCase().split("-")[0];

  return SUPPORTED_LANGUAGE_CODES.includes(base) ? base : DEFAULT_LANGUAGE;
};
